import React from "react";

import Typography from "@/components/Typography";

import { CardRowActionsProps, CardRowCellProps, CardRowCellsProps, CardRowProps } from "./types";

export const CardRow = React.forwardRef<HTMLElement, CardRowProps>(
  ({ children, className = "", as: Component = "div", ...props }, ref) => (
    <Component
      ref={ref}
      className={`mb-2 flex h-[94px] w-full flex-row items-center justify-between overflow-x-auto rounded-[24px] border-0 bg-white px-6 py-4 ${className}`}
      {...props}
    >
      {children}
    </Component>
  ),
);

CardRow.displayName = "CardRow";

export const CardRowDivider = ({ className = "" }: { className?: string }) => (
  <div className={`h-[62px] w-px bg-grey100 ${className}`} data-card-row-divider />
);

export const CardRowCell = ({
  label,
  value,
  icon,
  valueSuffix,
  renderValue,
  status,
  rightContent,
  className = "",
  valueProps,
}: CardRowCellProps) => {
  const displayedValue = value === undefined ? undefined : renderValue ? renderValue(value) : value;

  return (
    <div
      className={`flex h-[62px] min-w-0 items-center sm:min-w-[164px] ${className}`}
      data-card-row-cell
    >
      {icon && (
        <div className="ml-2 flex size-[30px] shrink-0 items-center justify-center rounded-full bg-grey100">
          {icon}
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col items-start overflow-hidden pl-4">
        <Typography variant="overline" color="grey500" className="font-bold uppercase">
          {label}
        </Typography>
        {status ? (
          <div
            className={`rounded-full px-[1.2rem] ${
              status.tone === "success" ? "bg-success200 text-black" : "bg-danger200 text-white"
            }`}
          >
            <Typography variant="pLarge" color={status.tone === "success" ? "black" : "white"}>
              {status.label}
            </Typography>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Typography variant="pLarge" color="black" {...valueProps}>
              {displayedValue}
              {valueSuffix}
            </Typography>
            {rightContent}
          </div>
        )}
      </div>
    </div>
  );
};

export const CardRowCells = ({
  children,
  divider = true,
  dividerClassName = "",
  className,
}: CardRowCellsProps) => {
  const childArray = React.Children.toArray(children);
  const isCardRowCell = (child: React.ReactNode) =>
    React.isValidElement<CardRowCellProps>(child) && child.type === CardRowCell;
  const firstCellIndex = childArray.findIndex(isCardRowCell);

  return (
    <div className={className || "flex items-center"}>
      {childArray.map((child, index) => {
        const isCell = isCardRowCell(child);

        return (
          <React.Fragment key={React.isValidElement(child) ? (child.key ?? index) : index}>
            {divider && isCell && index !== firstCellIndex && (
              <CardRowDivider className={dividerClassName || "mx-4"} />
            )}
            {child}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export const CardRowActions = ({ children, className = "" }: CardRowActionsProps) => (
  <div className={`flex items-center gap-2 ${className}`}>{children}</div>
);
